import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Copy, Link2, Eye, EyeOff } from "lucide-react";

export default function ShareLinksPage() {
  const [links, setLinks] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [workspaces, setWorkspaces] = useState<any[]>([]);
  const [selectedWs, setSelectedWs] = useState<string>("");
  const [showCreate, setShowCreate] = useState(false);
  const [newLink, setNewLink] = useState({ label: "", visibility: "busy_free", calendarAccountIds: "" });

  useEffect(() => {
    api.get("/api/v1/workspaces").then((ws) => {
      setWorkspaces(ws);
      if (ws.length > 0) setSelectedWs(ws[0].id.toString());
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedWs) return;
    api.get(`/api/v1/share-links?workspaceId=${selectedWs}`).then(setLinks).catch(() => {});
  }, [selectedWs]);

  const handleCreate = async () => {
    if (!newLink.label || !selectedWs) return;
    try {
      const result = await api.post("/api/v1/share-links", {
        workspaceId: parseInt(selectedWs),
        label: newLink.label,
        visibility: newLink.visibility,
        calendarAccountIds: newLink.calendarAccountIds.split(",").map(s => parseInt(s.trim())).filter(Boolean),
      });
      setLinks([{ id: result.id, token: result.token, ...newLink }, ...links]);
      setNewLink({ label: "", visibility: "busy_free", calendarAccountIds: "" });
      setShowCreate(false);
      toast.success("Share link created!");
    } catch (e: any) { toast.error(e.message); }
  };

  const handleDelete = async (id: number) => {
    if (!confirm("Delete this share link?")) return;
    try { await api.delete(`/api/v1/share-links/${id}`); setLinks(links.filter(l => l.id !== id)); toast.success("Deleted"); }
    catch (e: any) { toast.error(e.message); }
  };

  const copyIcalUrl = (token: string) => {
    const url = `${window.location.origin}/api/v1/ical/${token}`;
    navigator.clipboard.writeText(url);
    toast.success("iCal URL copied!");
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Share Links</h1>
        <div className="flex items-center gap-3">
          {workspaces.length > 0 && (
            <select value={selectedWs} onChange={(e) => setSelectedWs(e.target.value)} className="px-3 py-2 rounded-lg border border-input bg-background text-sm">
              {workspaces.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
            </select>
          )}
          <button onClick={() => setShowCreate(true)} className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 flex items-center gap-2">
            <Plus className="w-4 h-4" /> New Link
          </button>
        </div>
      </div>

      {showCreate && (
        <div className="bg-card rounded-xl border border-primary/30 p-5 mb-6">
          <h3 className="font-semibold mb-4">Create Share Link</h3>
          <div className="grid gap-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">Label</label>
              <input value={newLink.label} onChange={(e) => setNewLink({ ...newLink, label: e.target.value })} placeholder="e.g., Team Calendar" className="mt-1 w-full px-3 py-2 rounded-lg border border-input bg-background text-sm" />
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Visibility</label>
              <select value={newLink.visibility} onChange={(e) => setNewLink({ ...newLink, visibility: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-input bg-background text-sm">
                <option value="busy_free">Busy/Free only</option>
                <option value="title_only">Title only</option>
                <option value="full">Full details</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Calendar Account IDs (comma-separated)</label>
              <input value={newLink.calendarAccountIds} onChange={(e) => setNewLink({ ...newLink, calendarAccountIds: e.target.value })} placeholder="1, 2, 3" className="mt-1 w-full px-3 py-2 rounded-lg border border-input bg-background text-sm" />
            </div>
            <div className="flex gap-2">
              <button onClick={handleCreate} className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:opacity-90">Create</button>
              <button onClick={() => setShowCreate(false)} className="px-4 py-2 rounded-lg border border-input text-sm hover:bg-accent">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="animate-pulse space-y-3">{[1,2].map(i=><div key={i} className="h-20 bg-muted rounded-xl"/>)}</div>
      ) : links.length === 0 ? (
        <div className="text-center py-12">
          <Link2 className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <p className="font-medium">No share links yet</p>
          <p className="text-sm text-muted-foreground mt-1">Create a share link to generate an iCal subscription URL.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {links.map(l => (
            <div key={l.id} className="bg-card rounded-xl border border-border p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                {l.visibility === "full" ? <Eye className="w-5 h-5 text-primary" /> : <EyeOff className="w-5 h-5 text-muted-foreground" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{l.label}</p>
                <p className="text-xs text-muted-foreground">Visibility: {l.visibility} · {l.is_active ? "Active" : "Inactive"}</p>
              </div>
              <button onClick={() => copyIcalUrl(l.token)} className="p-2 rounded-lg hover:bg-accent text-muted-foreground" title="Copy iCal URL"><Copy className="w-4 h-4" /></button>
              <button onClick={() => handleDelete(l.id)} className="p-2 rounded-lg hover:bg-destructive/10 text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
